import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { teamApi } from "api/team";
import { setAccessToken } from "api/client";

/**
 * Public page (Sprint 11, Part 91 §91.1): accepts a team invite — the 72h
 * token from the invite link sets the member's password and activates them.
 */
export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await teamApi.acceptInvite({ token, password });
      setAccessToken(result.token);
      // Sprint 15 QA: only Owner/Manager do the setup wizard; a Rep/ReadOnly
      // member should land straight in the inbox (onboarding APIs are 403 for them).
      const role = result?.user?.role;
      navigate(role === "Owner" || role === "Manager" ? "/onboarding" : "/inbox", { replace: true });
    } catch (err) {
      setError(err?.message || "Unable to accept the invite. The link may have expired.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "var(--color-background)" }}
    >
      <div
        className="w-full max-w-md rounded-2xl border p-8"
        style={{ borderColor: "var(--color-border)", background: "var(--color-card)", boxShadow: "var(--shadow-lg)" }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Icon name="UserCheck" size={20} color="var(--color-primary)" />
          <h1 className="font-heading font-bold text-xl" style={{ color: "var(--color-foreground)" }}>
            Accept your invite
          </h1>
        </div>
        <p className="font-caption text-sm mb-6" style={{ color: "var(--color-text-secondary)" }}>
          You've been added to a workspace. Set a password to start — it needs at least 8 characters with an uppercase letter, a lowercase letter and a number.
        </p>

        {!token && (
          <p className="font-caption text-sm mb-4" style={{ color: "var(--color-error)" }}>
            This invite link is missing its token. Ask your workspace owner for a fresh link.
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="font-caption text-sm mb-1 block" style={{ color: "var(--color-foreground)" }}>
              Password
            </label>
            <input
              type="password"
              className="w-full text-sm px-3 py-2 rounded-lg border font-caption"
              style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="font-caption text-sm mb-1 block" style={{ color: "var(--color-foreground)" }}>
              Confirm password
            </label>
            <input
              type="password"
              className="w-full text-sm px-3 py-2 rounded-lg border font-caption"
              style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
          </div>

          {error && (
            <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{error}</p>
          )}

          <Button type="submit" loading={isSubmitting} disabled={!token} className="w-full">
            {isSubmitting ? "Accepting…" : "Accept invite & get started"}
          </Button>
        </form>

        <p className="font-caption text-sm mt-5 text-center" style={{ color: "var(--color-text-secondary)" }}>
          Already have an account? <Link to="/login" style={{ color: "var(--color-primary)" }}>Log in</Link>
        </p>
      </div>
    </div>
  );
}
